import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@repo/ui";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import QRCode from "@/components/qr-code";

export default function LookupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [lookupMethod, setLookupMethod] = useState<"phone" | "email">("phone");
  const [lookupValue, setLookupValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationUuid, setRegistrationUuid] = useState<string | null>(null);

  // Verification step
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [verified, setVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [verifiedName, setVerifiedName] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Look Up Registration | House & Condo Expo";
  }, []);

  // Check for id query parameter on mount (from QR code scan)
  // Now supports UUID, legacy_uid, or numeric ID
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (idParam && idParam.trim()) {
      setRegistrationUuid(idParam.trim());
    }
  }, [searchParams]);

  // Handle navigation state from edit page (after successful save)
  useEffect(() => {
    const state = location.state as {
      verified?: boolean;
      registrationUuid?: string;
      verificationToken?: string;
      verifiedName?: string;
    } | null;

    if (
      state?.verified &&
      state.registrationUuid &&
      state.verificationToken &&
      state.verifiedName
    ) {
      setRegistrationUuid(state.registrationUuid);
      setVerificationToken(state.verificationToken);
      setVerifiedName(state.verifiedName);
      setVerified(true);
      // Clear the state to prevent re-triggering on future navigations
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const onLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setRegistrationUuid(null);
    setFirstName("");
    setLastName("");
    setAttempts(0);
    setVerified(false);
    setVerificationToken(null);
    if (!lookupValue.trim()) {
      const message =
        lookupMethod === "phone"
          ? "กรุณากรอกเบอร์โทร / Enter phone number"
          : "กรุณากรอกอีเมล / Enter email address";
      setError(message);
      return;
    }
    setLoading(true);
    try {
      const queryParam = lookupMethod === "phone" ? "phone" : "email";
      const res = await fetch(
        `/api/registrations/lookup?${queryParam}=${encodeURIComponent(lookupValue.trim())}`
      );
      if (!res.ok) throw new Error("Not found");
      const data = (await res.json()) as { uuid: string; exists: boolean };
      setRegistrationUuid(data.uuid);
    } catch {
      setError("ไม่พบข้อมูล / Not found");
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError("กรุณากรอกชื่อและนามสกุล / Enter first and last name");
      return;
    }
    if (!registrationUuid) return;

    setVerifying(true);
    setAttempts(attempts + 1);
    try {
      const res = await fetch(`/api/registrations/${registrationUuid}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        }),
      });

      if (!res.ok) {
        if (res.status === 429) {
          setError("ลองมากเกินไป กรุณารอสักครู่ / Too many attempts. Please try again later.");
        } else {
          setError("ข้อมูลไม่ถูกต้อง / Verification failed");
        }
        return;
      }

      const data = (await res.json()) as { token: string; uuid: string; name: string };
      // Store token and show choice screen
      setVerificationToken(data.token);
      setVerifiedName(data.name);
      setVerified(true);
    } catch {
      setError("เกิดข้อผิดพลาด / An error occurred");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {verified
                ? `Hello ${verifiedName} / สวัสดีคุณ ${verifiedName}`
                : registrationUuid
                  ? "Verify Identity / ยืนยันตัวตน"
                  : "Look up registration / ค้นหาข้อมูล"}
            </CardTitle>
            <Button variant="outline" onClick={() => navigate("/")}>
              Back
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!registrationUuid ? (
            // Lookup form
            <form onSubmit={onLookup} className="space-y-3">
              <div className="space-y-2">
                <Label>Lookup Method / วิธีการค้นหา</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={lookupMethod === "phone" ? "default" : "outline"}
                    onClick={() => {
                      setLookupMethod("phone");
                      setLookupValue("");
                      setError(null);
                    }}
                    className="flex-1"
                  >
                    Phone / เบอร์โทร
                  </Button>
                  <Button
                    type="button"
                    variant={lookupMethod === "email" ? "default" : "outline"}
                    onClick={() => {
                      setLookupMethod("email");
                      setLookupValue("");
                      setError(null);
                    }}
                    className="flex-1"
                  >
                    Email / อีเมล
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lookupValue">
                  {lookupMethod === "phone" ? "Phone / เบอร์โทรศัพท์มือถือ" : "Email / อีเมล"}
                </Label>
                <Input
                  id="lookupValue"
                  type={lookupMethod === "email" ? "email" : "text"}
                  value={lookupValue}
                  onChange={(e) => setLookupValue(e.target.value)}
                  placeholder={lookupMethod === "phone" ? "0812345678" : "example@email.com"}
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Searching..." : "Search"}
              </Button>
            </form>
          ) : verified ? (
            // Choice screen after successful verification
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3">
                <QRCode uuid={registrationUuid!} size={192} />
                <div className="text-sm text-muted-foreground">UUID: {registrationUuid}</div>
                <p className="text-xs text-center text-muted-foreground">
                  Save this QR code or scan it to access your registration / บันทึก QR code
                  นี้หรือสแกนเพื่อเข้าถึงข้อมูลของคุณ
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  className="w-full text-wrap h-auto"
                  onClick={() =>
                    navigate(`/edit/${registrationUuid}?mode=coupon`, {
                      state: { token: verificationToken },
                    })
                  }
                >
                  Register Purchase for Coupon / ลงทะเบียนซื้อเพื่อรับคูปอง
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    navigate(`/edit/${registrationUuid}`, { state: { token: verificationToken } })
                  }
                >
                  Edit Registration / แก้ไขข้อมูล
                </Button>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setRegistrationUuid(null);
                    setError(null);
                    setFirstName("");
                    setLastName("");
                    setAttempts(0);
                    setVerified(false);
                    setVerificationToken(null);
                    setVerifiedName(null);
                    setLookupValue("");
                  }}
                >
                  Back to Search / กลับไปค้นหา
                </Button>
              </div>
            </div>
          ) : (
            // Verification form
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                เพื่อความปลอดภัย กรุณายืนยันตัวตนด้วยชื่อและนามสกุล / For security, please verify
                your identity with your first and last name.
              </p>
              <form onSubmit={onVerify} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name ชื่อ</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name นามสกุล</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                {attempts > 0 && attempts < 3 && !error && (
                  <p className="text-sm text-muted-foreground">
                    Attempts remaining: {3 - attempts}
                  </p>
                )}
                <div className="flex gap-2 flex-col">
                  <Button type="submit" disabled={verifying} className="w-full">
                    {verifying ? "Verifying..." : "Verify"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setRegistrationUuid(null);
                      setError(null);
                      setFirstName("");
                      setLastName("");
                      setAttempts(0);
                      setVerified(false);
                      setVerificationToken(null);
                      setVerifiedName(null);
                      setLookupValue("");
                    }}
                    className="w-full"
                  >
                    Back
                  </Button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
